import { Component, createRef } from 'preact';
import type { ComponentChildren } from 'preact';
import type NavigationControl from './navigation-control';
//react-swipe has no type definitions available
//@ts-ignore 
import ReactSwipe from 'react-swipe';
import IconComponent from '../indoor/icons';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import { lineString, point } from '@turf/helpers';
import length from '@turf/length';
import { getLocale, getLocaleLang } from 'helpers/locale';
import Icon from '../indoor/icons';

/**
 * @private
 */
type Props = {
    controller: NavigationControl,
    directions: woosmap.map.IndoorDirectionResult,
    highlightStep: (stepToHighlight: woosmap.map.GeoJSONFeature, fitBounds: boolean, tilt: boolean,
        soundEnabled?: boolean) => void,
    resetDirections: () => void,
    closeCb: () => void,
    //@ts-ignore
    setUserLocation: (lat: number, lng: number, level: number, bearing?: number, forceFocus: boolean, adjusted: boolean) => void,
    notifyUserDeviated: (currentLocation: woosmap.map.GeoJSONFeature) => void,
    notifyProximityToDest: () => void,
    selectedWaypoints?: woosmap.map.GeoJSONFeature[] | null | undefined,
    selectedDestination?: woosmap.map.GeoJSONFeature | null | undefined,
};
/**
 * @private
 */
type PanelState = {
    currentStep: number,
    overview: boolean,
    enableSound: boolean,
    enableFocus: boolean,
    userLocation: woosmap.map.GeoJSONFeature,
    prevUserLocationLevel: number,
    lastDeviationNotification: number,
    currentLeg: number,
};

/**
 * @private
 */
export default class NavigationWidget extends Component<Props, PanelState> {
    marker!: woosmap.map.Marker;
    controller!: NavigationControl;
    headerRef!: { current: any };
    footerRef!: { current: any };
    userIcon!: woosmap.map.Icon;
    userIcon3d!: woosmap.map.Icon;
    timeout: any;
    totalSteps: number;
    leashDistance: number;
    bearingToApply: number;
    prevUserLoc!: woosmap.map.GeoJSONFeature;
    levelWiseSteps: Array<any>;
    userDeviationInterval: number;
    proximityThreshold: number;
    proximityAlertRaised!: boolean;

    constructor(props: Props) {
        super();
        this.state = {
            currentStep: 0,
            overview: false,
            enableSound: true,
            enableFocus: false,
            userLocation: {} as woosmap.map.GeoJSONFeature,
            prevUserLocationLevel: 0,
            lastDeviationNotification: 0,
            currentLeg: 0,
        };
        this.levelWiseSteps = [];
        this.totalSteps = 0;
        this.leashDistance = 10;
        this.bearingToApply = 0;
        this.prevUserLoc = {} as woosmap.map.GeoJSONFeature;
        this.proximityAlertRaised = false;

        this.controller = props.controller;
        this.headerRef = createRef();

        this.setMapControls(false);
        this.userIcon = {
            url: `https://sdk.woosmap.com/map/assets/navigation-arrow.svg`,
            scaledSize: {
                height: 60,
                width: 60,
            },
            anchor: new woosmap.map.Point(30, 30)
        };
        this.userIcon3d = {
            url: `https://sdk.woosmap.com/map/assets/navigation-arrow-3d.svg`,
            scaledSize: {
                height: 60,
                width: 60,
            },
            anchor: new woosmap.map.Point(30, 30)
        };
        this.userDeviationInterval = 15000;
        this.proximityThreshold = 10; //In meters
        this.renderItinerary = this.renderItinerary.bind(this);
        this.renderOverview = this.renderOverview.bind(this);
        this.highlightStep = this.highlightStep.bind(this);
        this.renderInstructionHeader = this.renderInstructionHeader.bind(this);
        this.renderFooter = this.renderFooter.bind(this);
        this.showUserMarker = this.showUserMarker.bind(this);
        this.setMapControls = this.setMapControls.bind(this);
        this.recenterNavigation = this.recenterNavigation.bind(this);
        this.buildRouteFeatureCollection = this.buildRouteFeatureCollection.bind(this);
        this.locateNearestLandmarkForUser = this.locateNearestLandmarkForUser.bind(this);
        this.groupStepsByLevel = this.groupStepsByLevel.bind(this);
        this.computeTotalStepsUptoLevel = this.computeTotalStepsUptoLevel.bind(this);
        this.latchUserLocationOnRoute = this.latchUserLocationOnRoute.bind(this);
        this.controller._indoorRenderer.addListener('indoor_user_location', this.updateUserLocation.bind(this));
    }

    componentDidMount() {
        const userCurrentLocation = this.controller._indoorRenderer.getUserLocation();

        this.highlightStep(0);
        if (userCurrentLocation) {
            const userLocation = {
                type: 'Feature',
                id: -1,
                geometry: {
                    type: 'Point',
                    coordinates: [userCurrentLocation.position.lng, userCurrentLocation.position.lat]
                },
                properties: {
                    level: userCurrentLocation.level.toString(),
                    name: getLocale().indoor['Your Location'],
                    pk: '-1'
                }

            };
            //@ts-ignore
            this.latchUserLocationOnRoute(userLocation);
        }

        this.buildRouteFeatureCollection();
    }
    componentWillUnmount() {
        this.levelWiseSteps = [];
        this.setMapControls(true);
        if (this.marker) {
            this.marker.setMap(null);
        }
    }
    componentDidUpdate(_prevProps: Props, prevState: PanelState) {
        const {
            currentStep, overview, userLocation
        } = this.state;
        const { resetDirections, selectedWaypoints } = this.props;
        if (currentStep !== prevState.currentStep) {
            let prevStep = prevState.currentStep;
            ///* TO DO: IF THEY ASK FOR 3D ARROW WHILE JUMPING BETWEEN WAYPOINTS
            if(selectedWaypoints) {
                prevStep = currentStep - 1;
            }
            this.highlightStep(currentStep, prevStep);
        }
        if (userLocation !== prevState.userLocation) {
            // eslint-disable-next-line max-len
            if (prevState.userLocation.properties && userLocation.properties && parseInt((userLocation.properties as any).level) !== parseInt((prevState.userLocation.properties as any).level)) {
                this.setState({ prevUserLocationLevel: parseInt((prevState.userLocation.properties as any).level) });
            }
            this.latchUserLocationOnRoute(userLocation, prevState.userLocation);
            this._checkProximityToDestination();
        }
        const marker = this.marker;
        if (overview !== prevState.overview) {
            if (overview) {
                resetDirections();
                this.setMapControls(true);
                if (marker) {
                    marker.setIcon(this.userIcon);
                    marker.setPitchAlignment('map');
                }
            } else {
                this.highlightStep(currentStep);
                this.setMapControls(false);
                if (marker) {
                    marker.setIcon(this.userIcon3d);
                    marker.setPitchAlignment('viewport');
                }
            }
        }
    }
    setMapControls(enable: boolean) {
        const map = (this.controller._indoorRenderer as any).map;
        const mapboxMap = map ? (this.controller._indoorRenderer as any)._getMapboxMap(map) : null;
        if (map && mapboxMap) {
            const controls = map._container ? map._container.getElementsByClassName('mapboxgl-ctrl') : null;
            if (enable) {
                mapboxMap.dragRotate.enable();
                mapboxMap.touchPitch.enable();
                if (controls) {
                    for (const control of controls) {
                        control.style.display = 'flex';
                    }
                }
            } else {
                mapboxMap.dragRotate.disable();
                mapboxMap.touchPitch.disable();
                if (controls) {
                    for (const control of controls) {
                        if (!control.classList.contains('mapboxgl-ctrl-attrib')) {
                            control.style.display = 'none';
                        }
                    }
                }
            }
            mapboxMap.on('move', this._mapMovedHandler.bind(this));
        }

    }
    recenterNavigation() {
        const { userLocation } = this.state;
        const marker = userLocation.geometry ? (this.controller._indoorRenderer as any)._userMarker : this.marker;
        if (marker) {
            const map = (this.controller._indoorRenderer as any).map;
            if (map) {
                const mapboxMap = (this.controller._indoorRenderer as any)._getMapboxMap(map);
                mapboxMap.easeTo({
                    bearing: marker.getRotation(),
                    duration: 1000,
                    zoom: 20,
                    center: {
                        lat: marker.getPosition().lat(),
                        lng: marker.getPosition().lng(),
                    },
                });
            }
        }

    }
    showUserMarker(prevFeature: woosmap.map.GeoJSONFeature | null | undefined, feature: woosmap.map.GeoJSONFeature | null | undefined, reverse: boolean = false) {
        const { controller } = this.props;
        const { userLocation } = this.state;
        if (feature) {
            const map = (controller._indoorRenderer as any).map;
            const geoJSONData: woosmap.map.GeoJSONGeometry = feature.geometry;
            let coordinates: Array<woosmap.map.GeoJSONPosition> = [];
            if (geoJSONData.type === "LineString") {
                coordinates = (geoJSONData as woosmap.map.GeoJSONLineString).coordinates;
            }
            const lat = coordinates[0][1];
            const lng = coordinates[0][0];
            const bearing = parseInt((feature.properties as any).bearing) || 0;
            if (map) {
                if (!this.marker) {
                    const marker = new window.woosmap.map.Marker({
                        position: {
                            lat, lng,
                        },
                        icon: this.userIcon3d
                    });
                    marker.setMap(map);
                    marker.setRotationAlignment('map');
                    marker.setPitchAlignment('viewport');
                    marker.setRotation(bearing);
                    this.marker = marker;
                    if (userLocation.geometry !== undefined) {
                        marker.setOpacity(0);
                    }
                } else if (prevFeature) {
                    if (reverse) {
                        this.marker.setRotation(bearing);
                        if (this.marker && (this.marker as any).moveAlong) {
                            (this.marker as any).moveAlong([...coordinates].reverse(), bearing);
                        }
                    } else {
                        const geoJSONData: woosmap.map.GeoJSONGeometry = prevFeature.geometry;
                        let coordinates: Array<woosmap.map.GeoJSONPosition> = [];
                        if (geoJSONData.type === "LineString") {
                            coordinates = (geoJSONData as woosmap.map.GeoJSONLineString).coordinates;
                        }
                        if ((this.marker as any).moveAlong) {
                            (this.marker as any).moveAlong(coordinates, bearing);
                        }
                    }
                }
            }
        }
    }
    highlightStep(currentStep: number, prevStep?: number) {
        const { directions, highlightStep } = this.props;
        const { enableSound, userLocation } = this.state;
        let feature = null;
        let prevFeature = null;
        let index = 0;
        if (directions && directions.routes && directions.routes.length > 0) {
            const route = directions.routes[0];
            if (route.legs && route.legs.length > 0) {
                route.legs.forEach((leg) => {
                    if (leg && leg.steps) {
                        leg.steps.forEach((step) => {
                            if (index === currentStep) {
                                feature = {
                                    type: 'Feature',
                                    id: index,
                                    isLastStep: index === leg.steps.length - 1,
                                    properties: {
                                        id: `${route.legs.indexOf(leg)}_${leg.steps.indexOf(step)}`,
                                        floor: `${step.start_location.level}`,
                                        instruction: step.instruction ? step.instruction.summary : '',
                                        bearing: step.bearing_start ? `${step.bearing_start}` : `0`,
                                        icon: step.instruction && step.instruction.instruction_type ? step.instruction.instruction_type : 'caret-down',
                                    },
                                    geometry: {
                                        type: 'LineString',
                                        coordinates: step.polyline,
                                    },
                                };
                            }
                            if (index === prevStep) {
                                prevFeature = {
                                    type: 'Feature',
                                    id: index,
                                    properties: {
                                        id: `${route.legs.indexOf(leg)}_${leg.steps.indexOf(step)}`,
                                        floor: `${step.start_location.level}`,
                                        instruction: step.instruction ? step.instruction.summary : '',
                                        bearing: step.bearing_start ? `${step.bearing_start}` : `0`,
                                        icon: step.instruction && step.instruction.instruction_type ? step.instruction.instruction_type : 'caret-down',
                                    },
                                    geometry: {
                                        type: 'LineString',
                                        coordinates: step.polyline,
                                    },
                                };
                            }
                            index = index + 1;
                        });
                    }
                });
                if (feature) {
                    const isPrevStep = prevStep !== null && prevStep !== undefined && prevStep > currentStep;
                    highlightStep(feature, true, true, isPrevStep ? false : enableSound);
                    //@ts-ignore
                    this.bearingToApply = parseInt((feature.properties as any).bearing) || 0;
                    if (userLocation.geometry === undefined) {
                        this.showUserMarker(prevFeature, feature, isPrevStep);
                    }
                    if ('speechSynthesis' in window && !isPrevStep && enableSound) {
                        const msg = new SpeechSynthesisUtterance();
                        //@ts-ignore
                        msg.text = (feature.properties as any).instruction;
                        msg.lang = getLocaleLang().toLowerCase();
                        if (this.timeout) {
                            clearTimeout(this.timeout);
                        }
                        this.timeout = setTimeout(() => {
                            window.speechSynthesis.speak(msg);
                        }, 700);
                    }
                    if (isPrevStep) {
                        this.proximityAlertRaised = false;
                    }
                }
            }
        }

    }
    latchUserLocationOnRoute(currentUserLoc: woosmap.map.GeoJSONFeature, prevUserLoc?: woosmap.map.GeoJSONFeature) {
        const { setUserLocation } = this.props;
        if (currentUserLoc && currentUserLoc.geometry) {
            const geoJSONData: woosmap.map.GeoJSONGeometry = currentUserLoc.geometry;
            let coordinates: woosmap.map.GeoJSONPosition | undefined;
            if (geoJSONData.type === "Point") {
                coordinates = (geoJSONData as woosmap.map.GeoJSONPoint).coordinates;
            }
            if (coordinates && coordinates.length > 0) {
                const lat = coordinates[1];
                const lng = coordinates[0];
                if (this.marker && (this.controller._indoorRenderer as any).isUserInsideVenue(lat, lng)) {
                    this.marker.setOpacity(0);
                }
                const level = parseInt((currentUserLoc.properties as any).level);
                setUserLocation(lat, lng, level, this.bearingToApply, false, true);

                const userMarker = (this.controller._indoorRenderer as any)._userMarker;
                userMarker.setIcon(this.userIcon3d);
                userMarker.setPitchAlignment('viewport');
                const userTrail: Array<[number, number]> = [];
                if (prevUserLoc && prevUserLoc.geometry) {
                    const geoJSONData: woosmap.map.GeoJSONGeometry = prevUserLoc.geometry;
                    let coordinates: woosmap.map.GeoJSONPosition | undefined;
                    if (geoJSONData.type === "Point") {
                        coordinates = (geoJSONData as woosmap.map.GeoJSONPoint).coordinates;
                    }
                    if (coordinates && coordinates.length > 0) {
                        userTrail.push([coordinates[0], coordinates[1]]);
                        userTrail.push([lng, lat]);
                        if (userMarker && (userMarker as any).moveAlong) {
                            (userMarker as any).moveAlong(userTrail, this.bearingToApply);
                        }
                    }

                }
            }

        }
    }
    _mapMovedHandler() {
        const marker = this.marker;
        const map = (this.controller._indoorRenderer as any).map;
        if (marker && map) {
            this.setState({ enableFocus: !map.getBounds().contains(marker.getPosition()) });
        }

    }
    updateUserLocation() {
        const userCurrentLocation = this.controller._indoorRenderer.getUserLocation();
        if (!userCurrentLocation) return;
        const lat = userCurrentLocation.position.lat;
        const lng = userCurrentLocation.position.lng;
        if (!this.controller._indoorRenderer.isUserInsideVenue(lat, lng)) {
            return;
        }
        if (!userCurrentLocation.adjusted) {
            this.locateNearestLandmarkForUser();
        }

    }
    buildRouteFeatureCollection() {
        const { directions } = this.props;
        const routeSteps: any[] = [];
        if (directions && directions.routes && directions.routes.length > 0) {
            const route = directions.routes[0];
            if (route.legs && route.legs.length > 0) {
                route.legs.forEach((leg) => {
                    if (leg && leg.steps) {
                        this.totalSteps = this.totalSteps + leg.steps.length;
                        leg.steps.forEach((step) => {
                            routeSteps.push(step);
                        });
                    }
                });
            }
        }
        this.levelWiseSteps = this.groupStepsByLevel(routeSteps);
    }
    locateNearestLandmarkForUser() {
        const userCurrentLocation = this.controller._indoorRenderer.getUserLocation();
        if (!userCurrentLocation) return;
        const currentLevel = userCurrentLocation.level;
        const routeCoordinates: Array<[number, number]> = [];
        const routePolylineChunks: Array<[number, number]> = [];
        const { prevUserLocationLevel } = this.state;
        const prevLevelIndex = this.levelWiseSteps.findIndex(element => element.level === prevUserLocationLevel);
        const levelRepeated = this.levelWiseSteps.filter((element) => element.level === currentLevel);
        let stepsForLevel;

        //Handled for multi floor routes that go through the same level more than once in the entire itinerary.
        if (prevLevelIndex > 0 && levelRepeated.length > 1) {
            // eslint-disable-next-line max-len
            stepsForLevel = this.levelWiseSteps.find((element, index) => element.level === currentLevel && index > prevLevelIndex);
        } else {
            stepsForLevel = this.levelWiseSteps.find(element => element.level === currentLevel);
        }
        //Handled for multi-floor routes to get nearest point on a route for the level where the user is located
        if (stepsForLevel && stepsForLevel.steps) {
            stepsForLevel.steps.forEach((step: any, stepIndex: number) => {
                if (stepIndex === 0) {
                    routeCoordinates.push([step.start_location.lng, step.start_location.lat]);
                }
                routeCoordinates.push([step.end_location.lng, step.end_location.lat]);
                step.polyline.forEach((segment: any) => {
                    routePolylineChunks.push([segment[0], segment[1]]);
                });
            });
            const polylineChunksForRoute = lineString(routePolylineChunks);
            const mergedPolylinesForRoute = lineString(routeCoordinates);
            const userPt = point([userCurrentLocation.position.lng, userCurrentLocation.position.lat]);
            const calibratedUserLocation = nearestPointOnLine(polylineChunksForRoute, userPt);
            const userLocationNearLandmark = nearestPointOnLine(mergedPolylinesForRoute, userPt);
            const distanceApart = calibratedUserLocation.properties.dist * 1000;
            let userLocation: any = {
                type: 'Feature',
                id: -1,
                geometry: {
                    type: 'Point',
                    coordinates: [userCurrentLocation.position.lng, userCurrentLocation.position.lat]
                },
                properties: {
                    level: userCurrentLocation.level.toString(),
                    name: getLocale().indoor['Your Location'],
                    pk: '-1'
                }

            };
            if (distanceApart <= this.leashDistance) {
                const adjustedLat = calibratedUserLocation.geometry.coordinates[1];
                const adjustedLng = calibratedUserLocation.geometry.coordinates[0];
                userLocation = { ...userLocation, geometry: { type: 'Point', coordinates: [adjustedLng, adjustedLat] } };
                // Handled for computing the proper index to set for currentStep. Index derived from turf helper classes considers the route segments on a particular level.
                const stepsUptoCurrentLevel = this.computeTotalStepsUptoLevel(userCurrentLocation.level);
                if ((userLocationNearLandmark.properties.index + stepsUptoCurrentLevel) < this.totalSteps) {
                    this.setState({ currentStep: userLocationNearLandmark.properties.index + stepsUptoCurrentLevel });
                }
            } else {
                const { notifyUserDeviated } = this.props;
                const { lastDeviationNotification } = this.state;
                if (lastDeviationNotification === 0 ||
                    (Date.now() - lastDeviationNotification) > this.userDeviationInterval) {
                    this.setState({ lastDeviationNotification: Date.now() });
                    notifyUserDeviated(userLocation as woosmap.map.GeoJSONFeature);
                }
            }
            this.setState({ userLocation: userLocation as woosmap.map.GeoJSONFeature });
        }

    }
    groupStepsByLevel(steps: Array<any>): Array<any> {
        const instructionsByLevel: any[] = [];
        let lastStepsByLevel: any = { level: null, steps: [] };
        steps.forEach(step => {
            if (lastStepsByLevel.level !== step.start_location.level) {
                lastStepsByLevel = { level: step.start_location.level, steps: [] };
                instructionsByLevel.push(lastStepsByLevel);
            }
            lastStepsByLevel.steps.push(step);
        });
        return instructionsByLevel;
    }
    computeTotalStepsUptoLevel(currentLevel: number): number {
        const { prevUserLocationLevel } = this.state;
        const prevLevelIndex = this.levelWiseSteps.findIndex(element => element.level === prevUserLocationLevel);
        const levelRepeated = this.levelWiseSteps.filter((element) => element.level === currentLevel);
        let stepIndex;
        if (prevLevelIndex > 0 && levelRepeated.length > 1) {
            // eslint-disable-next-line max-len
            stepIndex = this.levelWiseSteps.findIndex((element, index) => element.level === currentLevel && index > prevLevelIndex);
        } else {
            stepIndex = this.levelWiseSteps.findIndex(element => element.level === currentLevel);
        }
        let counter = 0;
        let total = 0;
        if (stepIndex > 0) {
            while (counter < stepIndex) {
                total = total + this.levelWiseSteps[counter].steps.length;
                counter = counter + 1;
            }
        }
        return total;
    }
    _checkProximityToDestination() {
        const userCurrentLocation = this.controller._indoorRenderer.getUserLocation();
        const { currentStep } = this.state;
        const userLat = userCurrentLocation.position.lat;
        const userLon = userCurrentLocation.position.lng;
        const { directions } = this.props;
        if (directions && directions.routes && directions.routes.length > 0) {
            const route = directions.routes[0];
            if (route.legs && route.legs.length > 0) {
                route.legs.forEach((leg) => {
                    const destination = leg.end_location;
                    const distanceFromDest = this._calcDistanceBetweenPoints(userLat, userLon,
                        destination.lat, destination.lng);
                    const { notifyProximityToDest } = this.props;
                    if (!this.proximityAlertRaised && distanceFromDest <= this.proximityThreshold &&
                        currentStep === this.totalSteps - 1 && userCurrentLocation.level === destination.level) {
                        this.proximityAlertRaised = true;
                        notifyProximityToDest();
                    }
                });
            }
        }

    }
    _calcDistanceBetweenPoints(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const line = lineString([[lon1, lat1], [lon2, lat2]]);
        const lengthInKm = length(line);
        return lengthInKm * 1000;

    }
    _jumpToStep(leg: number) {
        const { directions } = this.props;
        if (directions && directions.routes && directions.routes.length > 0) {
            const route = directions.routes[0];
            if (route.legs && route.legs.length > 0) {
                const legToJump = route.legs[leg];
                if (legToJump && legToJump.steps && legToJump.steps.length > 0) {
                    //Count steps in selected leg and all previous legs to get the correct step index
                    let stepIndex = 0;
                    for (let i = 0; i < leg; i++) {
                        const previousLeg = route.legs[i];
                        stepIndex = stepIndex + previousLeg.steps.length;
                    }
                    // stepIndex = stepIndex + legToJump.steps.length;
                    this.setState({ currentStep: stepIndex, currentLeg: leg });
                }
            }
        }
    }
    _getLegGlobalBounds(legIndex: number): { start: number; end: number } {
        const legs = this.props.directions.routes[0].legs;
        let stepCounter = 0;

        for (let i = 0; i < legs.length; i++) {
            const steps = legs[i].steps ?? [];
            const legLength = steps.length;

            if (i === legIndex) {
                return { start: stepCounter, end: stepCounter + legLength - 1 };
            }
            stepCounter += legLength;
        }

        return { start: 0, end: 0 };
    }
    isAtStartOfCurrentLeg(): boolean {
        const { currentStep, currentLeg } = this.state;
        const { start } = this._getLegGlobalBounds(currentLeg);
        return currentStep === start;
    }
    isAtEndOfCurrentLeg(): boolean {
        const { currentStep, currentLeg } = this.state;
        const { end } = this._getLegGlobalBounds(currentLeg);
        return currentStep === end;
    }
    _jumpToNextLeg() {
        if (this.isAtEndOfCurrentLeg()) {
            this.setState(prev => ({ currentLeg: prev.currentLeg + 1 }));
        }
    }

    _jumpToPrevLeg() {
        if (this.isAtStartOfCurrentLeg()) {
            this.setState(prev => ({ currentLeg: prev.currentLeg - 1 }));
        }
    }


    getWaypointName(waypoint: any): string {
        const { selectedWaypoints } = this.props;
        if (selectedWaypoints && selectedWaypoints.length > 0) {
            const selectedWaypoint = selectedWaypoints.find(wp => wp.id && wp.id.toString() === waypoint);
            return selectedWaypoint && selectedWaypoint.properties && (selectedWaypoint.properties as any).name ? (selectedWaypoint.properties as any).name as string : '';
        }
        return '';
    }
    renderInstructionHeader(previousStep: woosmap.map.IndoorStep, step: woosmap.map.IndoorStep | null | undefined, index: number): ComponentChildren {
        const instruction = (step && step.instruction);
        const prevInstruction = (previousStep.instruction);
        return (<div className="woosmap-navigation-panel__steps-container" key={index}>
            <div className="woosmap-navigation-panel__step">
                <div className="woosmap-navigation-panel__step__current">
                    <IconComponent size={52} icon="nav-walk_straight_small" />
                    <div>{prevInstruction?.summary}. {getLocale().indoor.Then} {instruction?.summary} {!step && getLocale().indoor['you are arrived']}</div>
                </div>
                {step && <div className="woosmap-navigation-panel__step__next">
                    <IconComponent size={48} icon={`nav-${(instruction as any).instruction_type || 'walk_straight_small'}`} />
                    {previousStep.distance.value > 0 && <div>{getLocale().indoor.in} {previousStep.distance.text}</div>}

                </div>}
            </div>
        </div>);
    }

    renderItinerary(): ComponentChildren {
        const { directions } = this.props;
        const routeSteps: any[] = [];
        let previousStep: any = null;

        if (directions && directions.routes && directions.routes.length > 0) {
            const route = directions.routes[0];
            if (route.legs && route.legs.length > 0) {
                route.legs.forEach((leg) => {
                    if (leg && leg.steps) {
                        leg.steps.forEach((step) => {
                            if (previousStep) {
                                routeSteps.push(this.renderInstructionHeader(
                                    previousStep,
                                    step,
                                    routeSteps.length
                                ));
                            }
                            previousStep = step;
                        });
                    }
                });
            }
        }
        if (previousStep) {
            routeSteps.push(this.renderInstructionHeader(
                previousStep,
                null,
                routeSteps.length
            ));

        }
        return routeSteps;
    }

    renderOverview(): ComponentChildren {
        const { closeCb } = this.props;
        return <div className="woosmap-navigation-panel__overview">
            <button className="btn btn--bordered btn--quit" title={getLocale().indoor.Quit} onClick={closeCb}>{getLocale().indoor.Quit}</button>
            <button className="btn btn--bordered btn--primary btn--resume" title={getLocale().indoor.Overview} onClick={() => { this.setState({ overview: false }); }}>{getLocale().indoor.Resume}</button>
        </div>;
    }

    renderFooter(): ComponentChildren {
        const { closeCb, directions, selectedWaypoints, selectedDestination } = this.props;
        const { currentStep, enableSound, currentLeg, enableFocus } = this.state;

        let stepToDisplay: any = null;
        let index = 0;

        let duration = 0;          // remaining duration in current leg
        let totalDistance = 0;     // remaining distance in current leg

        let totalSteps = 0;
        let totalLegs = 0;

        let destinationName = '';
        let stopDuration = 0;      // full leg duration
        let stopDistance = 0;      // full leg distance

        let legStartIndex = 0;
        let legEndIndex = 0;

        if (directions?.routes?.length > 0) {
            const route = directions.routes[0];

            if (route.legs?.length > 0) {
                totalLegs = route.legs.length;
                let counter = 0;
                route.legs.forEach((leg, legIndex) => {
                    const stepsCount = leg.steps?.length ?? 0;

                    if (legIndex === currentLeg) {
                        legStartIndex = counter;
                        legEndIndex = counter + stepsCount - 1;
                    }

                    counter += stepsCount;
                });
                route.legs.forEach((leg, legIndex) => {
                    if (!leg?.steps) return;

                    totalSteps += leg.steps.length;

                    leg.steps.forEach((step) => {
                        // current step to display
                        if (index === currentStep) {
                            stepToDisplay = step;
                        }

                        // remaining duration & distance ONLY for current leg
                        if (
                            index >= currentStep &&
                            index <= legEndIndex &&
                            legIndex === currentLeg
                        ) {
                            duration += step.duration.value;
                            totalDistance += step.distance.value;
                        }

                        index++;
                    });

                    // full leg (stop-to-stop) values
                    if (legIndex === currentLeg) {
                        stopDuration += leg.duration.value;
                        stopDistance = Math.round(stopDistance + leg.distance.value);

                        const waypointId = (leg as any).waypoint;
                        destinationName = this.getWaypointName(waypointId);
                        if (waypointId === null) {
                            //User has reached final stop so now display the final destination poi name
                            const destProps = selectedDestination?.properties as any;
                            destinationName = destProps
                              ? (destProps[`name:${getLocaleLang().toLowerCase()}`] || destProps.name || '')
                              : '';
                        }
                    }
                });
            }
        }

        if (!stepToDisplay) {
            return null;
        }



        duration = Math.floor(duration / 60);
        totalDistance = Math.round(totalDistance)
        stopDuration = Math.round(stopDuration / 60);
        const durationLabel = duration > 0 ? `${duration} min` : getLocale().indoor['Less than 1min'];
        const distanceLabel = `${totalDistance} ${getLocale().indoor['meters']}`;
        const stopdurationLabel = stopDuration > 0 ? `${stopDuration} min` : getLocale().indoor['Less than 1min'];
        const stopdistanceLabel = `${stopDistance} ${getLocale().indoor['meters']}`;
        const isLastStep = selectedWaypoints && selectedWaypoints.length > 0
            && currentLeg >= totalLegs - 1
            && currentStep >= totalSteps - 1;

        return isLastStep ? (
            <div className="stops__final-leg">
                <button className="stops__final-leg__close-icon"
                    title={getLocale().indoor.Quit} onClick={closeCb}>
                    <Icon icon="close" />
                </button>
                <button className="btn btn--bordered btn--quit stops__final-leg__close-text"
                    title={getLocale().indoor.Quit} onClick={closeCb}>
                    {getLocale().indoor.Quit}
                </button>
                <div className="stops__final-leg__content">
                    <div className="stops__final-leg__heading">
                        <span>{getLocale().indoor['destination_in_sight']}</span>
                        <Icon icon="tour" />
                    </div>
                    <div className="stops__final-leg__subtitle">
                        {getLocale().indoor['continue_to_reach']
                            .replace('{distance}', distanceLabel)
                            .replace('{name}', destinationName)}
                    </div>
                </div>
            </div>
        ) : selectedWaypoints && selectedWaypoints.length > 0 ? (
            <div className="stops">
                {/* TOP */}
                <div className="stops__top">
                    <div className="stops__actions">
                        <button className="stops__icon-btn" title={getLocale().indoor.Quit} onClick={closeCb}>
                            <Icon icon="close" />
                        </button>
                        <div className="stops__map-controls">
                            <button className="btn btn--square btn--bordered btn--sound"
                                title="Sound"
                                onClick={() => this.setState({ enableSound: !enableSound })}>
                                <IconComponent size={32} icon={enableSound ? 'sound' : 'sound-mute'} />
                            </button>
                            {enableFocus && (
                                <button className="btn btn--square btn--bordered btn--recenter"
                                    title={getLocale().indoor.Center}
                                    onClick={() => this.recenterNavigation()}>
                                    <IconComponent size={32} icon="center-marker" />
                                </button>
                            )}
                            <button className="btn btn--square btn--bordered btn--overview"
                                title={getLocale().indoor.Overview}
                                onClick={() => this.setState({ overview: true })}>
                                <IconComponent size={32} icon="itinerary-overview" />
                            </button>
                        </div>
                    </div>

                    <div className="stops__nav">

                        {<button disabled={currentStep <= 0} className="stops__icon-btn" onClick={() => { this.headerRef.current.prev(); this._jumpToPrevLeg(); }} title={getLocale().indoor.Previous}><IconComponent size={18} icon="arrow-left" /></button>}
                        <span className="stops__subtext">{getLocale().indoor.leg_step}</span>
                        {currentStep < totalSteps - 1 && <button className="stops__icon-btn stops__icon-btn--primary" onClick={() => { this.headerRef.current.next(); this._jumpToNextLeg(); }} title={getLocale().indoor.Next}><IconComponent size={18} icon="arrow-right" /></button>}
                    </div>
                </div>

                {/* BOTTOM */}
                <div className="stops__bottom">
                    <div className="stops__info">
                        <div className="stops__duration">{durationLabel} - {distanceLabel}</div>
                        <div className="stops__subtitle">
                            {destinationName && `${getLocale().indoor['for']} ${destinationName}`}
                        </div>
                    </div>

                    {/* {currentLeg < totalLegs - 1 ? (
                        <button className="stops__cta" onClick={() => this._jumpToStep(currentLeg + 1)}>
                            {getLocale().indoor['go_to_stop']} {currentLeg + 1} {getLocale().indoor['of']} {totalLegs - 1}
                        </button>
                    ) : (
                        <div className="route-action">
                            <span className="route-action__text">
                                {getLocale().indoor['final_destination']}
                            </span>

                            <Icon icon="tour" />
                        </div>
                    )} */}
                    {/**
                     <div className="stops__actions">
                        {<button disabled={currentLeg <= 0} className="stops__icon-btn" onClick={() => this._jumpToStep(currentLeg - 1)} title={getLocale().indoor.Previous}>
                            <IconComponent icon="double-arrow-left" />
                        </button>} <span className="stops__subtext"> {getLocale().indoor.Steps} </span>
                        {currentLeg < (totalLegs - 1) ? <button className="stops__icon-btn stops__icon-btn--primary" onClick={() => this._jumpToStep(currentLeg + 1)} title={getLocale().indoor.Next}>
                            <IconComponent icon="double-arrow-right" />
                        </button> : (
                        <div className="route-action">
                            <span className="route-action__text">
                                {getLocale().indoor['final_destination']}
                            </span>

                            <Icon icon="tour" />
                        </div>
                    )}
                    </div>
                     */}
                    <div className="stops__actions">
                        {<button disabled={currentLeg <= 0} className="stops__icon-btn" onClick={() => this._jumpToStep(currentLeg - 1)} title={getLocale().indoor.Previous}>
                            <IconComponent icon="double-arrow-left" />
                        </button>} <span className="stops__subtext"> {getLocale().indoor.Steps} </span>
                        {<button disabled={currentLeg >= (totalLegs - 1)} className="stops__icon-btn stops__icon-btn--primary" onClick={() => this._jumpToStep(currentLeg + 1)} title={getLocale().indoor.Next}>
                            <IconComponent icon="double-arrow-right" />
                        </button>}
                    </div>

                </div>
                <div className="stops__mobile-controls">
                    <button className="btn btn--square btn--bordered btn--sound"
                        title="Sound"
                        onClick={() => this.setState({ enableSound: !enableSound })}>
                        <IconComponent size={32} icon={enableSound ? 'sound' : 'sound-mute'} />
                    </button>
                    {enableFocus && (
                        <button className="btn btn--square btn--bordered btn--recenter"
                            title={getLocale().indoor.Center}
                            onClick={() => this.recenterNavigation()}>
                            <IconComponent size={32} icon="center-marker" />
                        </button>
                    )}
                    <button className="btn btn--square btn--bordered btn--overview"
                        title={getLocale().indoor.Overview}
                        onClick={() => this.setState({ overview: true })}>
                        <IconComponent size={32} icon="itinerary-overview" />
                    </button>
                </div>
            </div>
        ) : (<div className="woosmap-navigation-panel__footer">
            <div className="woosmap-navigation-panel__footer__content">
                <div className="woosmap-navigation-panel__footer__content__left">
                    <div className="woosmap-navigation-panel__footer__current-level">
                        <span className="current-level__label">{getLocale().indoor.Level}</span>
                        <span className="current-level__number">{stepToDisplay.start_location.level}</span>
                    </div>
                    <button className="btn btn--bordered btn--quit" title={getLocale().indoor.Quit} onClick={closeCb}>{getLocale().indoor.Quit}</button>
                    <div className="woosmap-navigation-panel__footer__content__left__information">
                        <div className="woosmap-navigation-panel__steps-container" key={index}>
                            <IconComponent size={12} icon="nav-walk_straight_small" />
                            {stepToDisplay.distance.value > 0 && <div>{stepToDisplay.distance.text}</div>}&nbsp;-&nbsp;
                            {stepToDisplay.duration.text}
                        </div>
                        <div className="woosmap-navigation-panel__footer__content__title">{getLocale().indoor.ETA} {durationLabel}</div>
                    </div>
                </div>
                <div className="woosmap-navigation-panel__footer__content__right">
                    <div className="woosmap-navigation-panel__footer__content__right__options">
                        <button className="btn btn--square btn--bordered btn--sound" title={`Sound`} onClick={() => { this.setState({ enableSound: !enableSound }); }}><IconComponent size={32} icon={enableSound ? 'sound' : 'sound-mute'} /></button>
                        {enableFocus && <button className="btn btn--square btn--bordered btn--recenter" title={getLocale().indoor.Center} onClick={() => { this.recenterNavigation(); }}><IconComponent size={32} icon="center-marker" /></button>}
                        <button className="btn btn--square btn--bordered btn--overview" title={getLocale().indoor.Overview} onClick={() => { this.setState({ overview: true }); }}><IconComponent size={32} icon="itinerary-overview" /></button>
                    </div>
                    {currentStep > 0 && <button className="btn btn--square btn--bordered btn--previous" onClick={() => this.headerRef.current.prev()} title={getLocale().indoor.Previous}><IconComponent size={32} icon="arrow-left" /></button>}
                    {currentStep < totalSteps - 1 && <button className="btn btn--square btn--bordered btn--next" onClick={() => this.headerRef.current.next()} title={getLocale().indoor.Next}><IconComponent size={32} icon="arrow-right" /></button>}
                </div>
            </div>
        </div>);
    }

    render(): ComponentChildren {
        const { currentStep, overview } = this.state;
        if (overview) {
            return this.renderOverview();
        }
        return (
            <>

                <ReactSwipe
                    swipeOptions={{
                        continuous: false,
                        disableScroll: false,
                        stopPropagation: false,
                        startSlide: currentStep,
                        transitionEnd: (index: number) => {
                            this.setState({ currentStep: index });
                        }
                    }}

                    ref={this.headerRef}
                >
                    {this.renderItinerary()}
                </ReactSwipe>
                {this.renderFooter()}
            </>
        );
    }
}
